import { useMemo } from "react";

import numeral from "numeral";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { ChangeCell } from "@similarweb/pro-logic-components";

import { LeaderValueCell } from "../../../common/widget/table/LeaderValueCell";
import { rowIndexColumn } from "../../../common/widget/table/rowIndexColumn";
import { useDomainColumn } from "../../../common/widget/table/useDomainColumn";

import type { MetricKey, SocialMediaEngagementRow } from "./types";
import { METRIC_KEYS } from "./types";

const COLUMN_WIDTH = 110;

export const useSocialMediaEngagementColumns = (): ColumnsType<SocialMediaEngagementRow> => {
    const { translate } = useTranslate();
    const domainColumn = useDomainColumn({ dataAutomation: "social-engagement-domain-cell" });

    return useMemo(() => {
        const metricColumn = (key: MetricKey) => ({
            align: "right" as const,
            dataIndex: key,
            key,
            title: translate(`activation.alerts.social-media.metrics.engagement.column-title.${key}`),
            width: COLUMN_WIDTH,
            render: (data: SocialMediaEngagementRow[MetricKey]) => (
                <LeaderValueCell
                    dataAutomation={`social-engagement-${key}-cell`}
                    isLeader={data.isLeader}
                    value={data.value}
                />
            ),
        });

        const metricColumns = METRIC_KEYS.map(metricColumn);

        const engagementScoreColumn = {
            key: "engagementScore",
            title: translate("activation.alerts.social-media.metrics.engagement.column-title.engagement-score"),
            tooltipTitle: translate("activation.alerts.social-media.metrics.engagement.column-title.engagement-score"),
            tooltipSubtitle: translate("activation.alerts.social-media.metrics.engagement.tooltip.engagement-score"),
            children: [
                {
                    align: "right" as const,
                    dataIndex: "engagementScore" as const,
                    key: "engagementScore-score",
                    title: translate("activation.alerts.social-media.metrics.engagement.column-title.score"),
                    width: COLUMN_WIDTH,
                    render: (data: SocialMediaEngagementRow["engagementScore"]) => (
                        <LeaderValueCell
                            dataAutomation="social-engagement-score-cell"
                            isLeader={data.isLeader}
                            value={data.score}
                        />
                    ),
                },
                {
                    align: "right" as const,
                    dataIndex: "engagementScore" as const,
                    key: "engagementScore-change",
                    title: translate("global.change"),
                    width: COLUMN_WIDTH,
                    render: (data: SocialMediaEngagementRow["engagementScore"]) => (
                        <ChangeCell
                            dataAutomation="social-engagement-change-cell"
                            formattedValue={
                                data.change != null ? numeral(Math.abs(data.change)).format("0.00%") : undefined
                            }
                            value={data.change ?? undefined}
                        />
                    ),
                },
            ],
        };

        return [rowIndexColumn, domainColumn, ...metricColumns, engagementScoreColumn];
    }, [domainColumn, translate]);
};